import {
  ADMIN_EDIT_MANUAL_ORDER,
  AdminCustomProviderSelect,
  AdminDatePickerInput,
  AdminDollarInput2,
  AdminHeader,
  AdminInput,
  AdminSelect,
  AdminSingleBrandPicker,
  AdminSingleLocationPicker,
  AdminSpinner,
  Button,
  Div,
  ErrorToast,
  H3,
  P,
  Separator,
  SimpleLocationEntity,
  SuccessToast,
  createManualOrder,
  read,
  store,
  updateManualOrder,
  useOrders,
  reportError,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import 'react-datetime-picker/dist/DateTimePicker.css';
import { useLocation, useNavigate } from 'react-router';
import { OrderType, RecoveryStatus } from 'components/src/types/order.types';
import { Provider } from 'components/src/types/shared.types';
import { CreateManualOrderRequestData } from 'components/src/types/api/order.types';
import { BrandType, SimpleBrandEntity } from 'components/src/types/brand.types';

const orderTypeOptions = [
  { name: 'Delivery', value: OrderType.delivery },
  { name: 'Pickup', value: OrderType.pickup },
];

const renderTypeOptions = (
  <optgroup label="Select Order Type">
    {orderTypeOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const statuses = [
  { name: 'Adjustment', value: RecoveryStatus.ADJUSTMENT },
  { name: 'Evidence Needed', value: RecoveryStatus.EVIDENCE_NEEDED },
  { name: 'Not Eligible', value: RecoveryStatus.NOT_ELIGIBLE },
  { name: 'Pending', value: RecoveryStatus.PENDING },
  { name: 'Recovered', value: RecoveryStatus.RECOVERED },
];

const typeOptions = (
  <optgroup label="Recovery Status">
    {statuses.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

// Treat the picked calendar date as noon in the location's timezone, then send as UTC.
const toLocationNoonUTC = (jsDate: Date, timeZone?: string) =>
  DateTime.fromJSDate(jsDate)
    .setZone(timeZone, { keepLocalTime: true })
    .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    .toUTC()
    .toISO();

export const AddManualOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const locationLSNode = 'vgrubs-admin-orders-location';
  const locationFromLS = JSON.parse(read(locationLSNode) as string);

  // Get today's date
  const today = DateTime.now();

  // Subtract one day from today's date
  const yesterday = today.minus({ days: 1 }).toJSDate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [selectedRecoveryStatus, setSelectedRecoveryStatus] =
    useState<RecoveryStatus>(RecoveryStatus.PENDING);
  const [selectedLocation, setSelectedLocation] = useState(
    locationFromLS || null
  );
  const [selectedBrand, setSelectedBrand] = useState<SimpleBrandEntity | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState<OrderType>(
    orderTypeOptions[0].value
  );
  const [orderNumber, setOrderNumber] = useState('');
  const [customerFullName, setCustomerFullName] = useState('');
  const [total, setTotal] = useState(0);
  const [recoveryAmount, setRecoveryAmount] = useState(0);
  const [date, setDate] = useState([yesterday]);
  const [dateToSend, setDateToSend] = useState(
    toLocationNoonUTC(yesterday, selectedLocation?.timeZone)
  );

  const handleLocationsSubmit = (l: SimpleLocationEntity) => {
    setSelectedBrand(null);
    store(locationLSNode, JSON.stringify(l));
  };

  const { orderToEdit } = useOrders((state) => state);

  const isEdit = location.pathname === ADMIN_EDIT_MANUAL_ORDER;

  useEffect(() => {
    if (orderToEdit && isEdit) {
      setOrderType(orderToEdit.type);
      setOrderNumber(orderToEdit.number);
      setProvider(orderToEdit.provider);
      setCustomerFullName(orderToEdit.customer?.name || '');
      setTotal(orderToEdit.charges?.total || 0);
      setRecoveryAmount(orderToEdit.recovery?.amount || 0);
      setSelectedRecoveryStatus(
        orderToEdit.recovery?.status || RecoveryStatus.PENDING
      );
      if (orderToEdit.brand) {
        setSelectedBrand({
          id: orderToEdit.brand.id,
          name: orderToEdit.brand.name,
          isVGrubsBrand: orderToEdit.brand.type === BrandType.vGrubs,
          isLocation: orderToEdit.brand.isLocation,
        });
      }
      setDate([DateTime.fromISO(orderToEdit.date).toLocal().toJSDate()]);
      setDateToSend(orderToEdit.date);
    }
  }, [orderToEdit]);

  const pageGoBack = () => navigate(-1);

  async function handleAddManualOrder() {
    if (!provider || !dateToSend) return;
    const body: CreateManualOrderRequestData = {
      type: orderType,
      orderNumber,
      provider,
      brandId: selectedBrand?.id || undefined,
      customerName: customerFullName,
      total,
      recoveryAmount,
      recoveryStatus: selectedRecoveryStatus,
      createdAt: dateToSend,
      locationId: selectedLocation?.id,
    };

    if (
      !orderType ||
      !orderNumber ||
      !provider ||
      !customerFullName ||
      !total ||
      !selectedRecoveryStatus ||
      !date ||
      !selectedLocation
    ) {
      ErrorToast('Please fill in all information');
      return;
    }

    try {
      setLoading(true);

      if (isEdit && orderToEdit?.id) {
        delete body.brandId;
        delete body.locationId;

        const { data } = await updateManualOrder(orderToEdit?.id, body);

        SuccessToast(data.message);
      } else {
        const { data } = await createManualOrder(body);

        SuccessToast(data.message);
      }

      pageGoBack();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleValueRecoveryChange = useCallback((val: number) => {
    setRecoveryAmount(val);
  }, []);

  const handleValueTotalChange = useCallback((val: number) => {
    setTotal(val);
  }, []);

  const BrandComponent = () => {
    return (
      <div>
        <P $mb="8" $ml="24" $mr="24">
          <b>{'Select Brand'}</b>
        </P>
        <AdminSingleBrandPicker
          selectedLocation={selectedLocation}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
        />
      </div>
    );
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <AdminHeader title={'Create Order'}></AdminHeader>
      <H3 $m="40px 0" $mSM="32px 0">
        Order Details
      </H3>
      <Div $flex $gap="40px" $gapSM="20px" $columnSM>
        <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
          {!isEdit && (
            <div>
              <P $mb="8" $ml="24" $mr="24">
                <b>{'Select Location'}</b>
              </P>
              <AdminSingleLocationPicker
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                handleSubmit={(l) => handleLocationsSubmit(l)}
              />
            </div>
          )}
          {!isEdit && (
            <Div $mobile>
              <BrandComponent />
            </Div>
          )}
          <AdminCustomProviderSelect
            value={provider}
            onChange={
              setProvider as (
                provider: Provider | null | (Provider | null)[]
              ) => void
            }
            label="Provider"
          />
          <AdminSelect
            value={orderType}
            options={renderTypeOptions}
            onChange={(e) => setOrderType(e.target.value as OrderType)}
            label="Order Type"
          />
          <div>
            <P $mb="8" $ml="24" $mr="24">
              <b>Order Date</b>
            </P>
            <AdminDatePickerInput
              onChange={setDate}
              value={date}
              valueInput={dateToSend}
              click={() => {
                setDateToSend(
                  toLocationNoonUTC(date[0], selectedLocation?.timeZone)
                );
              }}
            />
          </div>
        </Div>
        <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
          {!isEdit && (
            <Div $desktop>
              <BrandComponent />
            </Div>
          )}
          <AdminInput
            value={orderNumber}
            onChange={({ target }: { target: { value: string } }) =>
              setOrderNumber(target.value)
            }
            label="Order Number"
            placeholder="Enter here"
          />

          <AdminInput
            value={customerFullName}
            onChange={({ target }: { target: { value: string } }) =>
              setCustomerFullName(target.value)
            }
            label="Customer Full Name"
            placeholder="Enter here"
          />

          <AdminDollarInput2
            label="Total"
            onValueChange={handleValueTotalChange}
            value={total}
          />
        </Div>
      </Div>
      <Separator $m="32px 0" $mSM="24px 0" />
      <Div $flex $gap="40px" $columnSM>
        <Div $width="50%" $widthSM="100%" $desktop></Div>
        <Div $width="50%" $widthSM="100%" $flex $gap="20px" $columnSM>
          <AdminSelect
            value={selectedRecoveryStatus}
            options={typeOptions}
            onChange={(e) =>
              setSelectedRecoveryStatus(e.target.value as RecoveryStatus)
            }
            placeholder="Select location"
            label="Recovery Status"
          />
          <AdminDollarInput2
            label="Recovery Amount"
            onValueChange={handleValueRecoveryChange}
            value={recoveryAmount}
          />
        </Div>
      </Div>
      <Div $flex $gap="40px" $mt="50" $columnSM>
        <Div $width="50%" $widthSM="100%" $desktop></Div>
        <Div $width="50%" $widthSM="100%" $flex $gap="20px" $columnSM>
          <Button $selected $gray $w="100%" onClick={pageGoBack}>
            Cancel
          </Button>
          <Button $selected $w="100%" onClick={handleAddManualOrder}>
            Save
          </Button>
        </Div>
      </Div>
    </>
  );
};
